/**
 * チャイム音マスタ管理画面 (要件 3・4)。
 *
 * 放送開始 / 終了時に鳴らすチャイム音 (ChimeMaster) を CRUD する GUI。
 * ControlPanel API の `/chimes` エンドポイント群を利用する。
 *
 * 機能:
 *   - 一覧表示 (表示名 / 音源キー / 更新日時 / 操作)
 *   - 新規登録フォーム (表示名 + MP3 ファイル選択)
 *   - 表示名のインライン編集 / 音源の差し替え (PUT)
 *   - 試聴 (`GET /chimes/{chime_id}/audio` → Audio 再生)
 *   - 削除 (ConfirmDialog で確認 → DELETE)。放送地点から参照中の場合は API が 409 を返し、
 *     その旨をエラーメッセージとして表示する (参照切れ防止)。
 *
 * 登録したチャイムは放送地点編集モーダルの「放送開始 / 終了チャイム」セレクタから
 * 選択でき、UpdateContents が `contents_schedule[]` の先頭 / 末尾に `is_chime: true` の
 * エントリとして差し込む (既存音源との ffmpeg 合成は行わない)。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { base64ToAudioUrl } from '../api/broadcastPoints';
import { ChimesClient } from '../api/chimes';
import { ApiError } from '../api/common';
import type { Chime } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { formatDateTime } from '../utils/dateTime';

import { ConfirmDialog } from './ConfirmDialog';

export interface ChimeMasterScreenProps {
  config: AppConfig;
}

/** 表示名の最大長 (バックエンド `MAX_CHIME_LABEL_LENGTH` と一致させる) */
const MAX_LABEL_LENGTH = 100;

/** インライン編集中の行の状態 */
interface EditState {
  /** 編集対象の chime_id */
  chime_id: string;
  /** 編集開始時の表示名 (変更判定に使う) */
  originalLabel: string;
  /** 入力中の表示名 */
  label: string;
  /** 差し替える音源ファイル (未選択なら現在の音源を維持) */
  file: File | null;
  /** 保存処理中フラグ */
  saving: boolean;
}

/** API クライアントの例外を画面表示用文字列に整形する */
function formatApiError(err: unknown): string {
  if (err instanceof ApiError) {
    // 409 は「放送地点から参照中」の意味を持つため、原因が伝わるよう補足を添える。
    if (err.status === 409) {
      return `[HTTP 409] ${err.message}（放送地点の設定を「（なし）」に変更してから削除してください）`;
    }
    return `[HTTP ${err.status}] ${err.message}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return '予期しないエラーが発生しました';
}

export function ChimeMasterScreen(props: ChimeMasterScreenProps): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();

  const apiClient = useMemo(() => new ChimesClient({ getIdToken, config }), [getIdToken, config]);

  const [items, setItems] = useState<Chime[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** 一覧取得の世代カウンタ (古いレスポンスで新しい結果を上書きしないための防御) */
  const requestIdRef = useRef(0);

  // --- 新規登録フォーム ---
  const [formLabel, setFormLabel] = useState('');
  const [formFile, setFormFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /**
   * ファイル入力への ref。
   * 登録成功後に `value = ''` で選択状態をクリアするために使う
   * (`File` は state 側でクリアできるが、input 要素の表示名が残ってしまう)。
   */
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- インライン編集 ---
  const [edit, setEdit] = useState<EditState | null>(null);

  // --- 削除確認 ---
  const [deleteTarget, setDeleteTarget] = useState<Chime | null>(null);
  const [deleting, setDeleting] = useState(false);

  // --- 試聴 ---
  const [previewingId, setPreviewingId] = useState<string | null>(null);

  /** チャイム一覧を取得して state に反映する。 */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.list();
      if (currentRequestId !== requestIdRef.current) return;
      setItems(res.items);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      setErrorMessage(formatApiError(err));
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient]);

  useEffect(() => {
    void reload();
    return () => {
      // アンマウント時に世代を進め、進行中リクエストの結果を破棄する。
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref は DOM ではなくリクエスト世代カウンタ
      ++requestIdRef.current;
    };
  }, [reload]);

  const canSubmit = formLabel.trim() !== '' && formFile !== null && !submitting && edit === null;

  /** 新規登録 (POST /chimes → リロード)。 */
  const handleAdd = useCallback(async (): Promise<void> => {
    if (!canSubmit || formFile === null) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await apiClient.create(formLabel.trim(), formFile);
      setFormLabel('');
      setFormFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await reload();
    } catch (err) {
      setErrorMessage(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }, [apiClient, canSubmit, formFile, formLabel, reload]);

  /** 指定行の編集を開始する。 */
  const startEdit = useCallback((chime: Chime): void => {
    setErrorMessage(null);
    setEdit({
      chime_id: chime.chime_id,
      originalLabel: chime.label ?? '',
      label: chime.label ?? '',
      file: null,
      saving: false,
    });
  }, []);

  const cancelEdit = useCallback((): void => {
    setEdit(null);
  }, []);

  const canSaveEdit =
    edit !== null &&
    !edit.saving &&
    edit.label.trim() !== '' &&
    (edit.label.trim() !== edit.originalLabel.trim() || edit.file !== null);

  /** 編集内容 (表示名 / 音源) を PUT で保存する。 */
  const handleSaveEdit = useCallback(async (): Promise<void> => {
    if (edit === null || !canSaveEdit) return;
    const target = edit;
    setEdit({ ...target, saving: true });
    setErrorMessage(null);
    try {
      await apiClient.update(target.chime_id, {
        label: target.label.trim(),
        ...(target.file ? { file: target.file } : {}),
      });
      setEdit(null);
      await reload();
    } catch (err) {
      setErrorMessage(formatApiError(err));
      setEdit((prev) => (prev ? { ...prev, saving: false } : prev));
    }
  }, [apiClient, canSaveEdit, edit, reload]);

  /** 削除確定 (DELETE /chimes/{chime_id} → リロード)。参照中の場合は 409。 */
  const handleDeleteConfirmed = useCallback(async (): Promise<void> => {
    if (!deleteTarget || deleting) return;
    const target = deleteTarget;
    setDeleting(true);
    setErrorMessage(null);
    try {
      await apiClient.remove(target.chime_id);
      setDeleteTarget(null);
      await reload();
    } catch (err) {
      // 409 (放送地点から参照中) 等はダイアログを閉じてエラー内容を提示する。
      setDeleteTarget(null);
      setErrorMessage(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  }, [apiClient, deleteTarget, deleting, reload]);

  /** 音源を取得して再生する (試聴)。 */
  const handlePreview = useCallback(
    async (chime: Chime): Promise<void> => {
      setErrorMessage(null);
      setPreviewingId(chime.chime_id);
      try {
        const { content_type, content_base64 } = await apiClient.getAudio(chime.chime_id);
        const audio = new Audio(base64ToAudioUrl(content_base64, content_type));
        await audio.play();
      } catch (err) {
        setErrorMessage(formatApiError(err));
      } finally {
        setPreviewingId(null);
      }
    },
    [apiClient],
  );

  return (
    <section className="section" data-testid="chime-master-section">
      <h2>チャイム音マスタ{loading ? ' (読み込み中...)' : ` (${items.length} 件)`}</h2>

      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#555' }}>
        放送開始 / 終了時に鳴らすチャイム音 (MP3) を登録します。
        登録した音源は放送地点の編集画面で「放送開始チャイム」「放送終了チャイム」として
        地点単位に選択できます。
      </p>

      {errorMessage ? (
        <div role="alert" className="error-message" data-testid="chime-master-error">
          {errorMessage}
        </div>
      ) : null}

      <form
        className="alert-recipient-form"
        data-testid="chime-master-form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleAdd();
        }}
      >
        <label>
          表示名
          <input
            type="text"
            value={formLabel}
            onChange={(e) => setFormLabel(e.target.value)}
            placeholder="例: ポーン（開始）"
            maxLength={MAX_LABEL_LENGTH}
            required
            data-testid="chime-form-label"
          />
        </label>
        <label>
          音源ファイル (MP3)
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mpeg,.mp3"
            onChange={(e) => setFormFile(e.target.files?.[0] ?? null)}
            required
            data-testid="chime-form-file"
          />
          <span className="field-hint">MP3 のみ / 最大 5 MiB</span>
        </label>
        <button
          type="submit"
          className="primary"
          disabled={!canSubmit}
          data-testid="chime-form-add-button"
        >
          {submitting ? '登録中...' : '登録'}
        </button>
        <button
          type="button"
          onClick={() => void reload()}
          disabled={edit !== null}
          data-testid="chime-master-reload-button"
        >
          再読み込み
        </button>
      </form>

      {items.length === 0 ? (
        <p className="data-table__empty" data-testid="chime-master-empty">
          {loading ? '読み込み中...' : '登録されているチャイム音がありません'}
        </p>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">表示名</th>
                <th scope="col">音源キー</th>
                <th scope="col">更新日時</th>
                <th scope="col">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((chime) => {
                const rowKey = encodeURIComponent(chime.chime_id);
                const isEditing = edit !== null && edit.chime_id === chime.chime_id;
                if (isEditing && edit) {
                  return (
                    <tr key={rowKey} data-testid={`chime-row-${rowKey}`}>
                      <td>
                        <input
                          type="text"
                          value={edit.label}
                          onChange={(e) =>
                            setEdit((prev) => (prev ? { ...prev, label: e.target.value } : prev))
                          }
                          aria-label={`${chime.label ?? chime.chime_id} の表示名(編集)`}
                          maxLength={MAX_LABEL_LENGTH}
                          disabled={edit.saving}
                          data-testid={`chime-edit-label-${rowKey}`}
                        />
                      </td>
                      <td>
                        <input
                          type="file"
                          accept="audio/mpeg,.mp3"
                          onChange={(e) =>
                            setEdit((prev) =>
                              prev ? { ...prev, file: e.target.files?.[0] ?? null } : prev,
                            )
                          }
                          aria-label={`${chime.label ?? chime.chime_id} の音源を差し替え`}
                          disabled={edit.saving}
                          data-testid={`chime-edit-file-${rowKey}`}
                        />
                        <span className="field-hint">未選択なら現在の音源を維持します</span>
                      </td>
                      <td>{formatDateTime(chime.updated_at)}</td>
                      <td>
                        <button
                          type="button"
                          className="primary"
                          onClick={() => void handleSaveEdit()}
                          disabled={!canSaveEdit}
                          data-testid={`chime-save-button-${rowKey}`}
                        >
                          保存
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={edit.saving}
                          data-testid={`chime-cancel-button-${rowKey}`}
                        >
                          キャンセル
                        </button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={rowKey} data-testid={`chime-row-${rowKey}`}>
                    <td>{chime.label ?? '—'}</td>
                    <td>
                      <code style={{ fontSize: 12 }}>{chime.audio_key ?? '—'}</code>
                    </td>
                    <td>{formatDateTime(chime.updated_at)}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => void handlePreview(chime)}
                        disabled={edit !== null || previewingId === chime.chime_id}
                        aria-label={`${chime.label ?? chime.chime_id} を試聴`}
                        data-testid={`chime-preview-button-${rowKey}`}
                      >
                        {previewingId === chime.chime_id ? '再生中...' : '▶ 試聴'}
                      </button>
                      <button
                        type="button"
                        onClick={() => startEdit(chime)}
                        disabled={edit !== null}
                        aria-label={`${chime.label ?? chime.chime_id} を編集`}
                        data-testid={`chime-edit-button-${rowKey}`}
                      >
                        編集
                      </button>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => setDeleteTarget(chime)}
                        disabled={edit !== null}
                        aria-label={`${chime.label ?? chime.chime_id} を削除`}
                        data-testid={`chime-delete-button-${rowKey}`}
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="チャイム音の削除"
        message={
          deleteTarget
            ? `チャイム音 "${deleteTarget.label ?? deleteTarget.chime_id}" を削除します。よろしいですか？\n` +
              '放送地点から参照中の場合は削除できません (先に放送地点側の設定を「（なし）」に変更してください)。'
            : ''
        }
        confirmLabel="削除"
        destructive
        confirmDisabled={deleting}
        cancelDisabled={deleting}
        onConfirm={() => void handleDeleteConfirmed()}
        onCancel={() => setDeleteTarget(null)}
      />
    </section>
  );
}
